import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fileToBase64, triggerNativeDownload } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, FileText, Download, Save, Loader2, ChevronRight, CheckCircle2, ChevronDown, Pencil, Eye, Image as ImageIcon, X as XIcon, Plus } from "lucide-react";
import { DOCUMENT_TEMPLATES } from "@/lib/docTemplates";

// Lazy-loaded HEIC converter — keeps the initial bundle small. Only fetched
// when an iPhone user actually picks a HEIC/HEIF photo.
let _heic2anyPromise = null;
const loadHeic2Any = () => {
  if (!_heic2anyPromise) _heic2anyPromise = import("heic2any").then((m) => m.default || m);
  return _heic2anyPromise;
};

const STAGE_BY_TPL = {
  sla: "sla",
  sales_confirmation: "sales_confirmation",
  commercial_invoice: "documentation",
  packing_list: "documentation",
  inspection: "documentation",
};

export default function ShipmentDocuments() {
  const { id, docId } = useParams();
  const { user } = useAuth();
  const isBuyer = user?.role === "buyer";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.get(`/shipments/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="p-6 text-stone-400">Loading…</div>;
  if (!data) return <div className="p-6 text-red-600">Shipment not found.</div>;
  if (isBuyer) return <div className="p-6 text-red-600">Document generator is for the operations team only.</div>;

  if (docId) {
    const tpl = DOCUMENT_TEMPLATES.find((t) => t.id === docId);
    if (!tpl) return <div className="p-6 text-red-600">Document type not found.</div>;
    return <DocumentEditor shipment={data.shipment} buyer={data.buyer} template={tpl} onSaved={reload} />;
  }

  return <DocumentList shipment={data.shipment} buyer={data.buyer} documents={data.documents} />;
}

// ─── List page ─────────────────────────────────────────────────────────────
function DocumentList({ shipment, buyer, documents }) {
  const generated = useMemo(() => {
    const map = {};
    (documents || []).forEach((d) => { if (d.template_id) map[d.template_id] = d; });
    return map;
  }, [documents]);
  const nav = useNavigate();
  const [busyTpl, setBusyTpl] = useState(null);

  const quickDownload = async (tpl) => {
    setBusyTpl(tpl.id);
    try {
      const ctx = { shipment, buyer };
      const values = tpl.defaults(ctx);
      const html = tpl.render(values, ctx);
      const filename = `${tpl.id}_${shipment.reference || "doc"}.pdf`;
      const { data } = await api.post(`/shipments/${shipment.id}/render-pdf-link`, { html, filename });
      // Native download trigger — works on iOS / Android / desktop / PWA / in-app browsers
      triggerNativeDownload(data.download_url);
      toast.success("PDF ready — check your downloads");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusyTpl(null); }
  };

  return (
    <div className="p-4 sm:p-5 space-y-4">
      <div className="flex items-center gap-3">
        <Link to={`/app/shipments/${shipment.id}`} className="text-stone-400 hover:text-[#6B0F1A]" data-testid="back-to-shipment">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Documents</div>
          <h1 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-[#1F1F1F] truncate">{buyer?.company_name || "—"}</h1>
          <div className="text-xs font-mono text-stone-500">{shipment.reference}</div>
        </div>
      </div>

      <div className="bg-[#F4E8E9]/40 border border-[#6B0F1A]/15 rounded-2xl p-4">
        <p className="text-sm text-stone-700">
          Generate, edit and download paperwork. Each document is prefilled from the buyer record — open <strong>Edit</strong> to tweak values, or <strong>Download</strong> straight away.
        </p>
      </div>

      <div className="space-y-2.5" data-testid="document-list">
        {DOCUMENT_TEMPLATES.map((tpl, idx) => {
          const existing = generated[tpl.id];
          return (
            <div key={tpl.id} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`doc-row-${tpl.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center shrink-0 font-display font-extrabold">{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1F1F1F] flex items-center gap-2 flex-wrap">
                    {tpl.title}
                    {existing && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Saved
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 truncate">{tpl.subtitle}</div>
                  {existing && (
                    <div className="text-[10px] text-stone-400 mt-0.5">
                      Last saved {new Date(existing.uploaded_at).toLocaleString()} · by {existing.uploaded_by_name || "—"}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => nav(`/app/shipments/${shipment.id}/documents/${tpl.id}`)}
                  className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-xs font-semibold rounded-lg px-3 py-1.5"
                  data-testid={`edit-${tpl.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={() => quickDownload(tpl)}
                  disabled={busyTpl === tpl.id}
                  className="inline-flex items-center gap-1.5 bg-white border border-stone-200 hover:border-[#6B0F1A] text-stone-700 hover:text-[#6B0F1A] text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50"
                  data-testid={`download-${tpl.id}`}
                >
                  {busyTpl === tpl.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Editor (form) ─────────────────────────────────────────────────────────
function DocumentEditor({ shipment, buyer, template, onSaved }) {
  const ctx = useMemo(() => ({ shipment, buyer }), [shipment, buyer]);
  const [values, setValues] = useState(() => template.defaults(ctx));
  const [tab, setTab] = useState("edit"); // "edit" | "preview"
  const [busy, setBusy] = useState(null);
  const [openAdvanced, setOpenAdvanced] = useState({});

  const set = (k, val) => setValues((s) => ({ ...s, [k]: val }));

  const buildHtml = () => template.render(values, ctx);

  const callRender = async (filename) => {
    const { data } = await api.post(`/shipments/${shipment.id}/render-pdf-link`, {
      html: buildHtml(),
      filename,
    });
    return data; // { token, filename, size, download_url }
  };

  const downloadPDF = async () => {
    setBusy("pdf");
    try {
      const filename = `${template.id}_${shipment.reference || "doc"}.pdf`;
      const meta = await callRender(filename);
      triggerNativeDownload(meta.download_url);
      toast.success("PDF ready — check your downloads");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  const saveAndAttach = async () => {
    setBusy("attach");
    try {
      const filename = `${template.id}_${shipment.reference || "doc"}.pdf`;
      const meta = await callRender(filename);
      // Pull the cached PDF as a blob to attach to the shipment
      const blobResp = await api.get(meta.download_url.replace(/^\/api/, ""), { responseType: "blob" });
      const blob = new Blob([blobResp.data], { type: "application/pdf" });
      const file = new File([blob], filename, { type: "application/pdf" });
      const content_base64 = await fileToBase64(file);
      await api.post(`/shipments/${shipment.id}/stage-uploads`, {
        stage: STAGE_BY_TPL[template.id] || "documentation",
        filename,
        content_base64,
        mime_type: "application/pdf",
        template_id: template.id,
      });
      toast.success("Attached to shipment");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-4 sm:p-5 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={`/app/shipments/${shipment.id}/documents`} className="text-stone-400 hover:text-[#6B0F1A]" data-testid="back-to-doc-list">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Document
            </div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight text-[#1F1F1F] truncate">{template.title}</h1>
            <div className="text-[11px] text-stone-500">{shipment.reference} · {buyer?.company_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={saveAndAttach}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 bg-white border border-stone-200 hover:border-[#6B0F1A] text-stone-700 hover:text-[#6B0F1A] text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50"
            data-testid="save-attach-btn"
          >
            {busy === "attach" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Save &amp; Attach</span>
          </button>
          <button
            onClick={downloadPDF}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50"
            data-testid="download-pdf-btn"
          >
            {busy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Download PDF</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 rounded-lg p-1 w-fit" data-testid="doc-tabs">
        <button
          onClick={() => setTab("edit")}
          className={`text-xs font-semibold rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors ${tab === "edit" ? "bg-white text-[#6B0F1A] shadow-sm" : "text-stone-600 hover:text-stone-800"}`}
          data-testid="tab-edit"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button
          onClick={() => setTab("preview")}
          className={`text-xs font-semibold rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors ${tab === "preview" ? "bg-white text-[#6B0F1A] shadow-sm" : "text-stone-600 hover:text-stone-800"}`}
          data-testid="tab-preview"
        >
          <Eye className="w-3 h-3" /> Preview
        </button>
      </div>

      {tab === "edit" ? (
        <div className="space-y-4">
          {template.sections.map((sec) => (
            <FormSection key={sec.id} section={sec} values={values} onChange={set} />
          ))}
          {template.photoGallery && (
            <PhotoGallery
              config={template.photoGallery}
              photos={values.photos || []}
              onChange={(photos) => set("photos", photos)}
            />
          )}
          {template.palletEditor && (
            <PalletEditor
              config={template.palletEditor}
              pallets={values.pallets || []}
              onChange={(pallets) => set("pallets", pallets)}
            />
          )}
          {template.advancedSections?.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-stone-500">Advanced — boilerplate clauses</div>
              {template.advancedSections.map((sec) => {
                const open = !!openAdvanced[sec.id];
                return (
                  <div key={sec.id} className="border-t border-stone-100">
                    <button
                      onClick={() => setOpenAdvanced((o) => ({ ...o, [sec.id]: !o[sec.id] }))}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-stone-50"
                      data-testid={`advanced-toggle-${sec.id}`}
                    >
                      <span className="font-semibold text-sm text-[#1F1F1F]">{sec.title}</span>
                      <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                    {open && (
                      <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {sec.fields.map((f) => <FormField key={f.key} field={f} value={values[f.key]} onChange={(v) => set(f.key, v)} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-7 doc-preview" data-testid="doc-preview"
          dangerouslySetInnerHTML={{ __html: buildHtml() }} />
      )}
    </div>
  );
}

function FormSection({ section, values, onChange }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`section-${section.id}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-[#6B0F1A] mb-3">{section.title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {section.fields.map((f) => (
          <FormField key={f.key} field={f} value={values[f.key]} onChange={(v) => onChange(f.key, v)} />
        ))}
      </div>
    </div>
  );
}

function FormField({ field, value, onChange }) {
  const colSpan = field.span === "full" || field.span === 2 ? "sm:col-span-2" : "";
  const baseInput = "w-full bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15";

  return (
    <div className={colSpan} data-testid={`field-wrap-${field.key}`}>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">{field.label}</label>
      {field.type === "textarea" ? (
        <textarea
          rows={field.rows || 3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={baseInput}
          data-testid={`field-${field.key}`}
        />
      ) : (
        <input
          type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={baseInput}
          data-testid={`field-${field.key}`}
        />
      )}
    </div>
  );
}


// ─── Photo gallery (used by templates with `photoGallery` config) ──────────
async function compressImageToDataUrl(file, maxDim = 1000, quality = 0.78) {
  // iOS captures photos as HEIC/HEIF which the browser <canvas> cannot decode.
  // Detect and transparently convert to JPEG using heic2any so users on iPhone
  // can upload directly without changing camera settings.
  const lowerName = (file.name || "").toLowerCase();
  const isHeic = file.type === "image/heic" || file.type === "image/heif"
    || lowerName.endsWith(".heic") || lowerName.endsWith(".heif");
  let working = file;
  if (isHeic) {
    try {
      const heic2any = await loadHeic2Any();
      const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.85 });
      const out = Array.isArray(blob) ? blob[0] : blob;
      working = new File([out], (file.name || "photo").replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
    } catch (err) {
      throw new Error("Could not convert HEIC photo. Please try a different image.");
    }
  }
  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = () => rej(new Error("Could not read file"));
    fr.readAsDataURL(working);
  });
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (!img.width || !img.height) {
        reject(new Error("Image appears to be empty or corrupt"));
        return;
      }
      const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      // JPEG keeps file size small for photographs (PNG bloats colour photos)
      const out = canvas.toDataURL("image/jpeg", quality);
      // Sanity-check: a successful encode produces a long base64 payload. Anything
      // ≤ ~200 chars is the browser's default empty data URL on a decode failure.
      if (!out || out.length < 500 || !out.startsWith("data:image/")) {
        reject(new Error("Could not encode image — please try a different format"));
        return;
      }
      resolve(out);
    };
    img.onerror = () => reject(new Error("Could not decode image"));
    img.src = dataUrl;
  });
}

function PhotoGallery({ config, photos, onChange }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const max = config.max || 12;

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset so same file can be picked again
    if (!files.length) return;
    const remaining = max - photos.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${max} photos allowed`);
      return;
    }
    const batch = files.slice(0, remaining);
    setBusy(true);
    setProgress({ done: 0, total: batch.length });
    let added = 0; let failed = 0;
    try {
      const next = [...photos];
      for (let i = 0; i < batch.length; i++) {
        const f = batch[i];
        const lower = (f.name || "").toLowerCase();
        const isImage = f.type.startsWith("image/") || /\.(jpe?g|png|webp|heic|heif)$/.test(lower);
        if (!isImage) { failed++; setProgress({ done: i + 1, total: batch.length }); continue; }
        try {
          const dataUrl = await compressImageToDataUrl(f);
          next.push({ id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, dataUrl, caption: "" });
          added++;
        } catch (err) {
          failed++;
          toast.error(`${f.name}: ${err.message}`);
        }
        setProgress({ done: i + 1, total: batch.length });
      }
      onChange(next);
      if (added > 0) toast.success(`${added} photo(s) added${failed ? `, ${failed} skipped` : ""}`);
    } catch {
      toast.error("Could not process images");
    } finally { setBusy(false); setProgress({ done: 0, total: 0 }); }
  };

  const updateCaption = (id, caption) => onChange(photos.map((p) => (p.id === id ? { ...p, caption } : p)));
  const removePhoto = (id) => onChange(photos.filter((p) => p.id !== id));

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid="photo-gallery">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#6B0F1A]">{config.title}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">{config.helper} · {photos.length}/{max}</div>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy || photos.length >= max}
          className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] disabled:opacity-50 text-white text-xs font-semibold rounded-lg px-3 py-1.5"
          data-testid="add-photo-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {busy && progress.total > 0 ? `${progress.done}/${progress.total}` : "Add photo"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif"
          multiple
          hidden
          onChange={onPick}
          data-testid="photo-input"
        />
      </div>

      {photos.length === 0 ? (
        <div className="border-2 border-dashed border-stone-200 rounded-xl py-8 text-center text-stone-400 text-sm flex flex-col items-center gap-2" data-testid="photo-empty">
          <ImageIcon className="w-8 h-8 opacity-50" />
          No photographs yet
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="photo-grid">
          {photos.map((p) => (
            <div key={p.id} className="group relative bg-stone-50 border border-stone-200 rounded-xl overflow-hidden flex flex-col" data-testid={`photo-${p.id}`}>
              <div className="relative aspect-[4/3] bg-stone-100">
                <img src={p.dataUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(p.id)}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center"
                  data-testid={`remove-photo-${p.id}`}
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <input
                type="text"
                placeholder="Caption (optional)"
                value={p.caption}
                onChange={(e) => updateCaption(p.id, e.target.value)}
                className="block w-full bg-white border-t border-stone-200 px-2.5 py-1.5 text-xs outline-none focus:bg-stone-50"
                data-testid={`caption-${p.id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Pallet editor (Packing List) ──────────────────────────────────────────
function PalletEditor({ config, pallets, onChange }) {
  const sizes = config.sizes || [14, 16, 18, 20, 22, 24];

  const addRow = () => {
    const next = [...pallets, {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      sizes: sizes.reduce((acc, s) => ({ ...acc, [s]: "" }), {}),
    }];
    onChange(next);
  };
  const removeRow = (id) => onChange(pallets.filter((p) => p.id !== id));
  const updateCell = (id, size, val) => onChange(pallets.map((p) =>
    p.id === id ? { ...p, sizes: { ...p.sizes, [size]: val.replace(/[^\d]/g, "") } } : p
  ));

  const palletTotal = (p) => sizes.reduce((acc, s) => acc + (parseInt(p?.sizes?.[s] || 0, 10) || 0), 0);
  const colTotal = (s) => pallets.reduce((acc, p) => acc + (parseInt(p?.sizes?.[s] || 0, 10) || 0), 0);
  const grandTotal = sizes.reduce((acc, s) => acc + colTotal(s), 0);

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid="pallet-editor">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#6B0F1A]">{config.title}</div>
          <div className="text-[11px] text-stone-500 mt-0.5">{config.helper}</div>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 bg-[#6B0F1A] hover:bg-[#56121C] text-white text-xs font-semibold rounded-lg px-3 py-1.5 shrink-0"
          data-testid="add-pallet-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add pallet
        </button>
      </div>

      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-xs border-collapse" data-testid="pallet-table">
          <thead>
            <tr className="bg-stone-50 text-stone-600 uppercase tracking-wider text-[10px]">
              <th className="px-2 py-2 text-left border-b border-stone-200 w-14">No.</th>
              {sizes.map((s) => (
                <th key={s} className="px-2 py-2 text-center border-b border-stone-200">Size {s}</th>
              ))}
              <th className="px-2 py-2 text-center border-b border-stone-200 bg-stone-100">Total</th>
              <th className="px-2 py-2 border-b border-stone-200 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {pallets.length === 0 ? (
              <tr>
                <td colSpan={sizes.length + 3} className="text-center text-stone-400 py-6 text-xs" data-testid="pallet-empty">
                  No pallets — click <strong>Add pallet</strong> to start.
                </td>
              </tr>
            ) : pallets.map((p, idx) => (
              <tr key={p.id} className="border-b border-stone-100" data-testid={`pallet-row-${idx + 1}`}>
                <td className="px-2 py-1.5 font-semibold text-stone-700">{idx + 1}</td>
                {sizes.map((s) => (
                  <td key={s} className="px-1 py-1.5">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={p.sizes?.[s] ?? ""}
                      onChange={(e) => updateCell(p.id, s, e.target.value)}
                      className="w-full text-center bg-white border border-stone-200 rounded px-1 py-1 outline-none focus:border-[#A8323F] focus:ring-1 focus:ring-[#A8323F]/20"
                      data-testid={`pallet-${idx + 1}-size-${s}`}
                    />
                  </td>
                ))}
                <td className="px-2 py-1.5 text-center font-bold bg-stone-50">{palletTotal(p) || ""}</td>
                <td className="px-1 py-1.5 text-center">
                  <button
                    type="button"
                    onClick={() => removeRow(p.id)}
                    className="text-stone-400 hover:text-red-600"
                    data-testid={`remove-pallet-${idx + 1}`}
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {pallets.length > 0 && (
              <tr className="bg-[#F4E8E9]/40 font-bold">
                <td className="px-2 py-2 uppercase tracking-wider text-[10px] text-[#6B0F1A]">Total</td>
                {sizes.map((s) => <td key={s} className="px-2 py-2 text-center">{colTotal(s) || ""}</td>)}
                <td className="px-2 py-2 text-center bg-stone-100">{grandTotal || ""}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
